import { createContext, useContext, useState, useEffect, useCallback, useRef} from "react";
import * as Api from "../utils/Api";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

const SatelliteContext = createContext();

export const SatelliteProvider = ({ children }) => {
    const [satNos, setSatNos] = useState([]);
    const satDataCacheRef = useRef({});
    const [isLoading, setIsLoading] = useState(false);

    // Fetch satellite numbers
    const fetchSatNos = async () => {
        try {
            const fetchedSatNos = await Api.getSatNos();
            if (!Array.isArray(fetchedSatNos)) {
                // Failed request ({ success: false }) or unexpected payload —
                // the API layer has already toasted the underlying error.
                return;
            }
            setSatNos([...fetchedSatNos].sort((a, b) => a - b));
        } catch (error) {
            toast.error(`Error fetching satellite numbers: ${error.message}`);
        }
    };

    // Export fetchSatData function
    useEffect(() => {
        fetchSatNos(); // Fetch satellite numbers on mount
    }, []);

    /**
     * Fetches satellite data for the given satellite numbers.
     *
     * This function checks if the requested satellite numbers are already cached.
     * If not, it fetches the data from the API and updates the cache. It also
     * handles cache size limits and can override existing cached data based on
     * the provided `override` parameter.
     *
     * @param {Array<number>} batchSatNos - An array of satellite numbers to fetch data for.
     * @param {boolean} [override=false] - A flag indicating whether to override existing cached data.
     * @returns {Promise<Object>} - A promise that resolves to an object containing the fetched or cached satellite data.
     */
    const fetchSatData = useCallback(async (batchSatNos, override = false) => {
        const satDataCache = satDataCacheRef.current;

        if (batchSatNos.length === 0) {
            setIsLoading(false);
            return {};
        }

        setIsLoading(true);

        const cachedData = {};
        let satNosToFetch = [];

        if (override) {
            for (const satNo of batchSatNos) {
                delete satDataCache[satNo];
            }
            satNosToFetch = batchSatNos;
        } else{
            for (const satNo of batchSatNos) {
                if (satDataCache[satNo]) {
                    cachedData[satNo] = satDataCache[satNo];
                } else {
                    satNosToFetch.push(satNo);
                }
            }
        }

        if (satNosToFetch.length > 0) {
            try {
                const response = await Api.getSatData(satNosToFetch.filter((s) => s).join(","));

                for (const key in response) {
                    if (Object.prototype.hasOwnProperty.call(response, key)) {
                        satDataCache[key] = response[key];
                        cachedData[key] = response[key];
                    }
                }

                setIsLoading(false); // Stop loading after fetching
                return cachedData; // Return the newly fetched data
            } catch (error) {
                toast.error(`Error fetching satellite data: ${error.message}`);
                setIsLoading(false); // Stop loading on error
                throw error; // Propagate error to be handled by top-level component
            }
        }

        setIsLoading(false); // Stop loading if no data to fetch
        return cachedData; // Return cached data if available
    }, []);

    // eslint-disable-next-line react-hooks/refs -- cache ref exposed for test assertions only
    const contextValue = { satNos, isLoading, fetchSatData, satDataCache: satDataCacheRef.current };

    return (
        <SatelliteContext.Provider value={contextValue}>
            {children}
        </SatelliteContext.Provider>
    );
};

SatelliteProvider.propTypes = {
    children: PropTypes.node.isRequired
};

// Custom hook to use the SatelliteContext
export const useSatellite = () => {
    return useContext(SatelliteContext);
};
